/**
 * Finding a garment by its design's short name and the digits after it (2026-10-17): the clerk taps
 * `BJB` and types only numbers on the phone's number pad — `3`, `36`, `3601` — and the design's
 * garments narrow as they type until `BJB-36-01` is the one left.
 *
 * <p>
 * <strong>Digits only, read as a prefix.</strong> A number pad has no space and no dash, so the
 * short way `readGarmentCode` reads (`bjb 36 1`) cannot be typed on one. Joining the code's digits
 * (`BJB-36-01` → `3601`, `BJB-38-R-01` → `3801`) and matching what is typed against the start of
 * them needs no separator, and every key pressed narrows the list rather than waiting for the end.
 * </p>
 */

/** The digits of a garment's code after its design's short name, or null if it is not that design's. */
export function codeDigits(serialCode: string, designCode: string): string | null {
  const prefix = `${designCode.trim().toUpperCase()}-`;
  const code = serialCode.trim().toUpperCase();

  return code.startsWith(prefix) ? code.slice(prefix.length).replace(/\D/g, '') : null;
}

/**
 * The design's garments that what is typed could still be, in code order, and the one it names
 * exactly when every digit has been typed. Nothing typed yet lists them all.
 */
export function pickByDigits<T extends { serialCode: string }>(
  garments: readonly T[],
  designCode: string,
  typed: string,
): { matches: T[]; exact: T | null } {
  const digits = typed.replace(/\D/g, '');

  const matches = garments
    .filter((garment) => codeDigits(garment.serialCode, designCode)?.startsWith(digits) ?? false)
    .sort((a, b) => a.serialCode.localeCompare(b.serialCode));

  const exact =
    digits === ''
      ? null
      : (matches.find((garment) => codeDigits(garment.serialCode, designCode) === digits) ?? null);

  // Only when nothing else could still be meant: `3601` names one garment, but if `36010` existed
  // the clerk may not have finished typing.
  return { matches, exact: exact && matches.length === 1 ? exact : null };
}

/** The design short names to offer, once each, in order — retired designs are not offered. */
export function designShortNames(
  designs: readonly { id: number; code: string; name: string; isActive: boolean }[],
): { id: number; code: string; name: string }[] {
  const seen = new Set<string>();

  return designs
    .filter((design) => design.isActive && design.code.trim() !== '')
    .map((design) => ({
      id: design.id,
      code: design.code.trim().toUpperCase(),
      name: design.name,
    }))
    .filter((design) => (seen.has(design.code) ? false : (seen.add(design.code), true)))
    .sort((a, b) => a.code.localeCompare(b.code));
}

/** The short names a search box narrows to: a code starting with it, or a name containing it. */
export function filterShortNames<T extends { code: string; name: string }>(
  names: readonly T[],
  search: string,
): T[] {
  const needle = search.trim().toUpperCase();

  return needle === ''
    ? [...names]
    : names.filter(
        (name) => name.code.startsWith(needle) || name.name.toUpperCase().includes(needle),
      );
}
